import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import JsonLd from "./components/JsonLd";
import Header from "./components/Header";
import { testimonials } from "./data/mock";
import Footer from "./components/Footer";
import StickyContactBar from "./components/StickyContactBar";
import QuoteModal from "./components/QuoteModal";
import { QuoteProvider } from "./context/QuoteContext";
import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import ServiceDetail from "./pages/ServiceDetail";
import ServiceAreaPage from "./pages/ServiceAreaPage";
import ServiceAreasIndex from "./pages/ServiceAreasIndex";
import GalleryInstall from "./pages/GalleryInstall";
import WhiteGloveInstall from "./pages/WhiteGloveInstall";
import SurroundSoundInstall from "./pages/SurroundSoundInstall";
import InstantQuote from "./pages/InstantQuote";
import RecentInstalls from "./pages/RecentInstalls";
import NotFound from "./pages/NotFound";
import HelloTechAlternative from "./pages/HelloTechAlternative";
import FireplaceTvMounting from "./pages/FireplaceTvMounting";
import { serviceAreas } from "./data/serviceAreas";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import TawkTo from "./components/TawkTo";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import { initLeadSource } from "./lib/leadSource";
import { useReveal } from "./lib/useReveal";

initLeadSource();

const SITE = "https://www.setuphometheater.com";
const LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_home-cinema-build/artifacts/ntpr30th_SHT-Logo-Tranparent-Txt.webp";

const SERVICE_CATALOG = [
  { name: "TV Installation & Wall Mounting", path: "/services/tv-installation" },
  { name: "Audio Installation", path: "/services/audio-installation" },
  { name: "Home Cinema Theater Installation", path: "/services/home-cinema" },
  { name: "Commercial AV Installation", path: "/services/commercial-av" },
  { name: "Religious Venue Audio", path: "/services/religious-venue" },
  { name: "Smart Home Device Setup", path: "/services/smart-home" },
  { name: "Security Camera Setup", path: "/services/security-cameras" },
  { name: "Lutron Motorized Shading", path: "/services/motorized-shading" },
];

const AREA_NAMES = ["Rocky Mount", "Wilson", "Greenville", "Tarboro", "Roanoke Rapids", "Nashville", "Smithfield", "Goldsboro", "Zebulon", "Wendell", "Louisburg", "Oxford", "Henderson", "Raleigh", "Red Oak", "Spring Hope", "Bailey"];

// "Starting at" price framework (USD) keyed by service path.
const PRICE_FROM = {
  "/services/tv-installation": 179,
  "/services/audio-installation": 79,
  "/services/home-cinema": 299,
  "/services/smart-home": 399,
};

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE}/#business`,
  name: "Set Up Home Theater",
  image: LOGO,
  logo: LOGO,
  telephone: "+1-252-545-0011",
  email: "chris@setuphometheater.com",
  url: SITE,
  priceRange: "$$",
  currenciesAccepted: "USD",
  paymentAccepted: "Cash, Credit Card",
  slogan: "We bring your home & business to life",
  sameAs: [
    "https://www.facebook.com/setuphometheater",
    "https://www.instagram.com/setuphometheater",
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Rocky Mount",
    addressRegion: "NC",
    postalCode: "27803",
    addressCountry: "US",
  },
  areaServed: [
    ...AREA_NAMES.map((n) => ({ "@type": "City", name: `${n}, North Carolina` })),
    { "@type": "AdministrativeArea", name: "Nash County, North Carolina" },
    { "@type": "AdministrativeArea", name: "Edgecombe County, North Carolina" },
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+1-252-545-0011",
      contactType: "customer service",
      areaServed: "US",
      availableLanguage: "English",
    },
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    reviewCount: "21",
    bestRating: "5",
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    opens: "08:00",
    closes: "19:00",
  },
  review: testimonials.slice(0, 6).map((t) => ({
    "@type": "Review",
    author: { "@type": "Person", name: t.name },
    reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
    reviewBody: t.text,
  })),
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Audio-Visual & Home Theater Services",
    itemListElement: [
      ...SERVICE_CATALOG.map((s) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: s.name, url: `${SITE}${s.path}` },
        ...(PRICE_FROM[s.path]
          ? { priceSpecification: { "@type": "PriceSpecification", minPrice: PRICE_FROM[s.path], priceCurrency: "USD" } }
          : {}),
      })),
      {
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: "Structured Cabling & Data Drops" },
        priceSpecification: { "@type": "UnitPriceSpecification", price: 125, priceCurrency: "USD", unitText: "per drop" },
      },
    ],
  },
  knowsAbout: [
    "TV wall mounting", "Home theater design", "Whole-home audio", "Commercial AV",
    "Smart home automation", "Lutron motorized shading", "Security camera installation",
  ],
};

const MarketingLayout = () => (
  <>
    <Header />
    <Outlet />
    <Footer />
    <QuoteModal />
    <StickyContactBar />
  </>
);

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useReveal();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return undefined; }
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 400);
    return () => clearTimeout(t);
  }, [pathname, hash]);
  return null;
};

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <JsonLd data={localBusinessSchema} />
        <QuoteProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Routes>
                <Route element={<MarketingLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/rocky-mount-nc" element={<ServiceDetail slug="rocky-mount-nc" />} />
                  <Route path="/tv-mounting-quote" element={<InstantQuote />} />
                  <Route path="/hellotech-alternative" element={<HelloTechAlternative />} />
                  <Route path="/tv-mounting-above-fireplace" element={<FireplaceTvMounting />} />
                  <Route path="/recent-installs" element={<RecentInstalls />} />
                  <Route path="/frame-tv-installation" element={<GalleryInstall />} />
                  <Route path="/white-glove-tv-installation" element={<WhiteGloveInstall />} />
                  <Route path="/surround-sound-installation" element={<SurroundSoundInstall />} />
                  <Route path="/service-areas" element={<ServiceAreasIndex />} />
                  {serviceAreas.map((a) => (
                    <Route key={a.slug} path={`/${a.slug}-nc`} element={<ServiceAreaPage slug={a.slug} />} />
                  ))}
                  <Route path="/services" element={<ServiceDetail slug="all" />} />
                  <Route path="/services/:slug" element={<ServiceDetail />} />
                  <Route path="/manufacturers" element={<Navigate to="/" replace />} />
                  <Route path="/manufacturers/*" element={<Navigate to="/" replace />} />
                  <Route path="/about-us" element={<Navigate to="/about" replace />} />
                  <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
                  <Route path="/tv-mounting" element={<Navigate to="/services/tv-installation" replace />} />
                  <Route path="/tv-installation" element={<Navigate to="/services/tv-installation" replace />} />
                  <Route path="/tv-wall-mounting" element={<Navigate to="/services/tv-installation" replace />} />
                  <Route path="/soundbar-installation" element={<Navigate to="/services/tv-installation" replace />} />
                  <Route path="/audio-installation" element={<Navigate to="/services/audio-installation" replace />} />
                  <Route path="/home-theater" element={<Navigate to="/services/home-cinema" replace />} />
                  <Route path="/religious-venue-audio-installation" element={<Navigate to="/services/religious-venue" replace />} />
                  <Route path="/smart-home" element={<Navigate to="/services/smart-home" replace />} />
                  <Route path="/areas-we-serve" element={<Navigate to="/services/areas-we-serve" replace />} />
                  <Route path="*" element={<NotFound />} />
                </Route>

                <Route path="/store/*" element={<Navigate to="/" replace />} />

                <Route path="/payment/*" element={<Navigate to="/" replace />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminDashboard />} />
              </Routes>
              <Toaster />
              <SonnerToaster />
              <TawkTo />
            </BrowserRouter>
        </QuoteProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
